import { Button, Pagination, Spinner, TextInput } from 'flowbite-react';
import React, { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';
import { useGetDriveCompaniesQuery } from 'src/hooks/drives.hooks';
import NoRecruiterPositionsContainer from 'src/components/recruiterPositions/NoRecruiterPositionsContainer';
import AMCompanyCard from 'src/components/accountManager/AMCompanyCard';
import { MyCompanyType } from 'types';
import AMCompanyDrawer from 'src/components/accountManager/AMCompanyDrawer';
import { TbSearch } from 'react-icons/tb';
import { debounce } from 'lodash';
import { useMediaQuery } from '@mui/material';

const DriveCompaniesPage = () => {
	const limit = 12;
	const navigate = useNavigate();
	const [currentPage, setCurrentPage] = useState(1);
	const isMobile = useMediaQuery('(max-width:960px)');
	const [searchParams, setSearchParams] = useSearchParams();
	const { data, isLoading } = useGetDriveCompaniesQuery({
		data: { name: searchParams.get('search') || '', limit, page: currentPage },
	});
	const [isCompanyDrawerOpen, setIsCompanyDrawerOpen] = React.useState(false);
	const [company, setCompany] = React.useState<MyCompanyType['company']>();
	const handleViewCompany = (company: MyCompanyType['company']) => {
		setCompany(company);
		setIsCompanyDrawerOpen(true);
	};

	const changeHandler = (search: string) => {
		searchParams.set('search', search);
		setSearchParams(searchParams);
	};

	const debouncedChangeHandler = useCallback(debounce(changeHandler, 1000), []);

	return (
		<div className="flex-1 w-full max-w-[1440px] mx-auto px-4 xl:px-20 flex flex-col">
			<div className="flex justify-between items-center py-2 pt-6 sm:pt-[10px] border-b border-[#E4E4EB] dark:border-[#555770] border-solid">
				<h4 className="text-xl font-semibold dark:text-white text-[#1C1C28]">Placement Drive Companies</h4>
				<div>
					<Button
						className="whitespace-pre"
						onClick={() => {
							navigate('/upcoming-drives');
						}}
						color="purplePlaced"
					>
						Register for New Drive
					</Button>
				</div>
			</div>

			<div className="pt-4 w-full flex flex-col md:flex-row justify-between items-center gap-3">
				{/* Search Input */}
				<TextInput
					icon={() => <TbSearch color="#C7C9D9" />}
					className="w-full [&_input]:bg-white max-w-full md:max-w-[522px]"
					placeholder="Search Company"
					name="search"
					rightIcon={() => (isLoading ? <Spinner /> : null)}
					onChange={e => {
						debouncedChangeHandler(e.target.value);
					}}
				/>

				{data?.companies?.length ? (
					<Pagination
						showIcons
						color="purplePlaced"
						className="-mt-2"
						layout={isMobile ? 'navigation' : 'pagination'}
						currentPage={currentPage}
						totalPages={Math.ceil(data?.total / limit)}
						onPageChange={page => {
							searchParams.set('page', String(page || 1));
							setSearchParams(searchParams);
							if (page !== currentPage) {
								setCurrentPage(page);
							}
						}}
					/>
				) : null}
			</div>

			{isLoading ? (
				<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="md:!w-[35%] mx-auto" />
			) : data?.companies?.length ? (
				<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 py-5">
					{data?.companies?.map(company => (
						<AMCompanyCard
							key={company?.company?._id}
							isActive={true}
							companyObj={company}
							handleViewCompanyDetails={handleViewCompany}
							onClick={() => {
								navigate('/drive-positions/' + company?.company?._id);
							}}
						/>
					))}
				</div>
			) : (
				<div className="my-auto flex flex-col items-center justify-center">
					<NoRecruiterPositionsContainer
						titleCss="!text-xl !font-semibold"
						title={'No companies registered for drive'}
						description={
							"You haven't registered for any upcoming drives yet. Register now to view available candidates."
						}
						buttonText={'Register for New Drive'}
						onClick={() => {
							navigate('/upcoming-drives');
						}}
					/>
				</div>
			)}

			{data?.companies?.length ? (
				<div className="flex justify-center">
					<Pagination
						showIcons
						color="purplePlaced"
						className="-mt-2"
						layout={isMobile ? 'navigation' : 'pagination'}
						currentPage={currentPage}
						totalPages={Math.ceil(data?.total / limit)}
						onPageChange={page => {
							searchParams.set('page', String(page || 1));
							setSearchParams(searchParams);
							if (page !== currentPage) {
								setCurrentPage(page);
							}
						}}
					/>
				</div>
			) : null}

			<AMCompanyDrawer
				company={company}
				isCompanyDrawerOpen={isCompanyDrawerOpen}
				setIsCompanyDrawerOpen={setIsCompanyDrawerOpen}
				closeDrawer={() => setIsCompanyDrawerOpen(false)}
			/>
		</div>
	);
};

export default DriveCompaniesPage;
